import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { normalizeHexColor, parseStatusColorConfig } from '../../utils/statusColor';

export type BadgeVariant = 'default' | 'secondary' | 'accent' | 'danger' | 'warning' | 'info' | 'success';

export interface BadgeProps extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'color'> {
  /** Semantic theme variant (ignored when `color` is set). */
  variant?: BadgeVariant;
  /**
   * Custom background color (hex or status-color config string).
   * When set, badge uses this background with white text.
   */
  color?: string | null;
  children: React.ReactNode;
}

/** Map Active/Inactive (or Yes/No) style flags to a badge variant. */
export const statusVariantFromBoolean = (active: boolean): BadgeVariant =>
  active ? 'success' : 'secondary';

const BADGE_BASE =
  'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white';

/**
 * Shared app badge — same pill shape and white text everywhere.
 * Use `variant` for semantic colors, or `color` for a custom hex/status color.
 */
const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  color,
  children,
  className,
  style,
  ...props
}) => {
  const { colors } = useTheme();
  const variantClasses: Record<BadgeVariant, string> = {
    default: 'bg-slate-500',
    secondary: 'bg-slate-500',
    accent: colors.accent,
    danger: colors.error,
    warning: colors.warning,
    info: colors.info,
    success: colors.success,
  };

  const parsed = color != null && color !== '' ? parseStatusColorConfig(color) : null;
  const resolvedColor = parsed ? normalizeHexColor(parsed.backgroundColor) : null;

  if (resolvedColor) {
    return (
      <span
        className={cn(BADGE_BASE, 'border border-transparent', className)}
        style={{
          backgroundColor: resolvedColor,
          borderColor: resolvedColor,
          color: '#ffffff',
          ...style,
        }}
        {...props}
      >
        {children}
      </span>
    );
  }

  return (
    <span
      className={cn(BADGE_BASE, variantClasses[variant], className)}
      style={style}
      {...props}
    >
      {children}
    </span>
  );
};

export interface ColoredBadgeProps {
  label: React.ReactNode;
  color?: string | null;
  className?: string;
  fallback?: React.ReactNode;
}

/** Convenience wrapper: `<ColoredBadge label="X" color="#…" />` → shared Badge. */
export const ColoredBadge: React.FC<ColoredBadgeProps> = ({
  label,
  color,
  className,
  fallback = '-',
}) => {
  if (label == null || label === '') {
    return <>{fallback}</>;
  }
  return (
    <Badge color={color} className={className}>
      {label}
    </Badge>
  );
};

export default Badge;
